import axios, { type AxiosError, type AxiosResponse } from 'axios'

/**
 * Configuration interface for the Incense Encryption Service
 */
export interface IncenseEncryptionConfig {
  /** Base URL for the incense backend API */
  baseUrl: string
  /** Request timeout in milliseconds */
  timeout?: number
  /** Retry configuration */
  retry?: {
    attempts: number
    delay: number
  }
}

/**
 * Response interface for sensitive data encryption
 */
export interface EncryptionResponse {
  /** Unique identifier for the encrypted data */
  sensitiveId: string
}

/**
 * Response interface for retrieving encrypted data
 */
export interface RetrievalResponse {
  /** Success message */
  message: string
  /** Unique identifier for the encrypted data */
  id: string
  /** Whether the data was successfully retrieved */
  retrieved: boolean
}

/**
 * Service for encrypting sensitive data via HTTP calls to the Incense backend
 *
 * This service provides a clean abstraction for making API calls to encrypt
 * sensitive data before it gets sent to external logging services.
 */
export class IncenseEncryptionService {
  private readonly config: Required<IncenseEncryptionConfig>
  private readonly axiosInstance

  constructor(config: IncenseEncryptionConfig) {
    this.config = {
      timeout: 5000,
      retry: {
        attempts: 3,
        delay: 1000,
      },
      ...config,
    }

    // Create axios instance with default configuration
    this.axiosInstance = axios.create({
      baseURL: this.config.baseUrl,
      timeout: this.config.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  /**
   * Encrypts sensitive data by sending it to the Incense backend
   *
   * @param sensitiveData - The sensitive data object to encrypt
   * @returns Promise resolving to the encryption response
   * @throws Error if encryption fails after all retry attempts
   */
  async encryptSensitiveData(sensitiveData: Record<string, unknown>): Promise<EncryptionResponse> {
    let lastError: Error | null = null

    for (let attempt = 1; attempt <= this.config.retry.attempts; attempt++) {
      try {
        const response: AxiosResponse = await this.axiosInstance.post('/v1/incense/sensitive', {
          payload: sensitiveData,
        })

        // Return the sensitiveId from the backend response
        return { sensitiveId: response.data.sensitiveId }
      } catch (error) {
        lastError = this.handleAxiosError(error)

        if (attempt < this.config.retry.attempts) {
          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, this.config.retry.delay))
        }
      }
    }

    throw new Error(
      `Failed to encrypt sensitive data after ${this.config.retry.attempts} attempts: ${lastError?.message}`,
    )
  }

  /**
   * Retrieves encrypted data by ID
   *
   * @param id - The encryption ID to retrieve
   * @returns Promise resolving to the retrieval response
   */
  private async getEncryptedData(id: string): Promise<RetrievalResponse> {
    try {
      const response: AxiosResponse<RetrievalResponse> = await this.axiosInstance.get(
        `/v1/incense/sensitive/${id}`,
      )
      return response.data
    } catch (error) {
      throw this.handleAxiosError(error)
    }
  }

  /**
   * Health check for the Incense backend service
   *
   * @returns Promise resolving to true if service is healthy
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response: AxiosResponse = await this.axiosInstance.get('/health')
      return response.status >= 200 && response.status < 300
    } catch {
      return false
    }
  }

  /**
   * Handles Axios errors and converts them to standard Error objects
   *
   * @param error - The error from axios
   * @returns Error object with meaningful message
   */
  private handleAxiosError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError

      if (axiosError.response) {
        // Server responded with error status
        return new Error(`HTTP ${axiosError.response.status}: ${axiosError.response.statusText}`)
      } else if (axiosError.request) {
        // Request was made but no response received
        return new Error(`Network error: ${axiosError.message}`)
      } else {
        // Something else happened
        return new Error(`Request setup error: ${axiosError.message}`)
      }
    }

    // Non-axios error
    return error instanceof Error ? error : new Error(String(error))
  }
}
